import { KitButton, KitText } from '@omni/kit/components';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { useState } from 'react';
import { Platform, ScrollView, ScrollViewProps, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import PlatformKeyboardSpacer from '../../../../mobile/components/PlatformKeyboardSpacer';

export type AttendanceMode = 'create' | 'edit';

interface Props {
  navigation: any;
  route: any;
  mode: AttendanceMode;
  onCancel?: (open?: boolean) => void;
}
const CreateOrUpdateGroupAttendance: React.FC<Props> = ({
  navigation,
  route,
  mode,
  onCancel,
}) => {
  const { windowHeight, windowWidth } = useSizeClass();
  const isLandscape = windowHeight < windowWidth;
  const { sizeClass } = useSizeClass();
  const { t } = useTranslation();
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(true);
  const handleScroll = (event: {
    nativeEvent: {
      contentOffset: any;
      layoutMeasurement: any;
      contentSize: any;
    };
  }) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;

    const isAtTop = contentOffset.y <= 0;
    const isAtBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height;

    // Show the top shadow when scrolling starts and hide it only at the very top
    setShowTopShadow(!isAtTop);

    // Show the bottom shadow always unless at the very end
    setShowBottomShadow(!isAtBottom);
  };

  return (
    <>
      {Platform.OS === 'web' ? (
        <View
          style={[
            {
              padding: Spacing.xl,
              paddingBottom: Spacing.l,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
            showTopShadow && {
              backgroundColor: Colors.N0,
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowOffset: { width: 0, height: 3 },
              shadowOpacity: 1,
              shadowRadius: 10,
              zIndex: 999,
            },
            sizeClass === SizeClass.Small && {
              alignItems: 'center',
            },
          ]}
        >
          <KitText black bold fontSize={20}>
            {mode === 'edit'
              ? t('groups:editAttendance')
              : t('groups:takeAttendanceButton')}
          </KitText>
        </View>
      ) : null}
      <ScrollViewWrapper
        onScroll={handleScroll}
        keyboardShouldPersistTaps='handled'
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
      >
        <View style={{ flex: 1, padding: 16 }}>
          {mode === 'create' || route?.params?.mode === 'create' ? (
            // This will change according the ui
            <KitText> {t('groups:takeAttendanceButton')}</KitText>
          ) : (
            <KitText>{t('groups:editAttendance')}</KitText>
          )}
        </View>
      </ScrollViewWrapper>
      {Platform.OS === 'web' || isLandscape ? (
        <View
          style={[
            {
              paddingVertical: Spacing.xl,
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              flexDirection: 'row-reverse',
              width: '100%',
              gap: sizeClass === SizeClass.Small ? 0 : Spacing.m,
            },
            showBottomShadow && {
              backgroundColor: Colors.N0,
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowOffset: { width: 0, height: -3 },
              shadowOpacity: 1,
              shadowRadius: 10,
              zIndex: 999,
            },
            sizeClass === SizeClass.Small && {
              flexDirection: 'row',
              justifyContent: 'center',
            },
          ]}
        >
          <KitButton
            secondary
            color={Colors.N100}
            title={t('common:buttonTitleCancel')}
            paddingLeft={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
            paddingRight={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
            style={{
              marginRight: Spacing.m,
            }}
            onPress={() => onCancel?.(false)}
          />
          <KitButton
            color={Colors.N900}
            paddingLeft={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
            paddingRight={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
            title={
              mode === 'edit'
                ? t('common:submitButtonTitle')
                : t('common:buttonTitleSave')
            }
            // disabled={}
            // isLoading={}
            onPress={() => console.log('save')}
            testID={mode}
          />
        </View>
      ) : null}
    </>
  );
};
const ScrollViewWrapper = (props: ScrollViewProps) => {
  return (
    <>
      <KeyboardAwareScrollView
        {...props}
        style={{ flex: 1, backgroundColor: Colors.N0 }}
        extraScrollHeight={Platform.OS === 'web' ? 0 : 70}
      >
        {props.children}
        <PlatformKeyboardSpacer />
      </KeyboardAwareScrollView>
    </>
  );
};

export default CreateOrUpdateGroupAttendance;
